import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

type ModalHonorarioProps = {
  open: boolean;
  onClose: () => void;
};

const WHATSAPP_PHONES = [
  { label: "(41) 98712-1009", link: "https://wa.me/5541987121009" },
  { label: "(41) 98735-1356", link: "https://wa.me/5541987351356" },
  { label: "(41) 98746-5631", link: "https://wa.me/5541987465631" },
  { label: "(41) 98806-3142", link: "https://wa.me/5541988063142" },
];

export default function ModalHonorario({ open, onClose }: ModalHonorarioProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = "modal-honorario-title";
  const descId = "modal-honorario-desc";

  useEffect(() => {
    if (!open || typeof window === "undefined") return;

    closeBtnRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || typeof window === "undefined") return null;

  const modal = (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onClick={onClose}
    >
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title id={titleId}>
          Você possui dívidas de honorários sucumbenciais
        </Title>

        <Text id={descId}>
          Sua dívida referente aos honorários sucumbenciais é devida à{" "}
          <strong>Bellinati Perez</strong>. Para negociação, entre em contato
          com nossos atendentes, via WhatsApp:
        </Text>

        <PhonesList>
          {WHATSAPP_PHONES.map(({ label, link }) => (
            <li key={link}>
              <PhoneLink href={link} target="_blank" rel="noopener noreferrer">
                {label}
              </PhoneLink>
            </li>
          ))}
        </PhonesList>

        <Actions>
          <CloseBtn ref={closeBtnRef} onClick={onClose}>
            Fechar
          </CloseBtn>
        </Actions>
      </Dialog>
    </Overlay>
  );

  return createPortal(modal, document.body);
}

const fadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;

const popIn = keyframes`
  from { transform: translateY(8px); opacity: 0 }
  to { transform: translateY(0); opacity: 1 }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  padding: 16px;
  z-index: 99999;
  animation: ${fadeIn} 120ms ease-out;
`;

const Dialog = styled.div`
  width: min(520px, 100%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
  padding: 20px 20px 16px;
  text-align: center;
  animation: ${popIn} 150ms ease-out;
`;

const Title = styled.h2`
  margin: 0 0 6px 0;
  font-size: 1.15rem;
  line-height: 1.3;
`;

const Text = styled.p`
  margin: 8px 0 0;
  line-height: 1.6;
`;

const PhonesList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
  justify-items: center;

  li {
    margin: 0;
  }
`;

const PhoneLink = styled.a`
  text-decoration: none;
  font-weight: 600;
  color: #0a66c2;
  border-radius: 6px;
  padding: 6px 10px;
  transition: background 120ms ease, color 120ms ease, transform 80ms ease;

  &:hover {
    background: rgba(10, 102, 194, 0.08);
  }
  &:focus-visible {
    outline: 2px solid #0a66c2;
    outline-offset: 2px;
  }
  &:active {
    transform: translateY(1px);
  }
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

const CloseBtn = styled.button`
  padding: 10px 16px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  background: #7d1416;
  color: #fff;
  transition: filter 120ms ease, transform 80ms ease, box-shadow 120ms ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:focus-visible {
    outline: 2px solid #7d1416;
    outline-offset: 2px;
    box-shadow: 0 0 0 4px rgba(125, 20, 22, 0.2);
  }
  &:active {
    transform: translateY(1px);
  }
`;
